<template>
  <section
    class="my-8 md:mb-8 mx-auto bg-black pt-6 px-2 md:px-10 pb-0 shadow-black shadow-xl divide-y-2 divide-white font-helvetica text-[22px] text-[#999999] max-w-[1400px]"
  >
    <RallyLogoDownload />

    <div class="pt-8 mt-8 flex flex-col gap-4">
      <h1 class="text-white text-2xl md:text-4xl font-russo">
        Rally Tulip Diagrams Explained
      </h1>
      <h2 class="font-semibold text-white">
        How Roadbook Tulips Show Every Junction
      </h2>
      <p class="text-[17px] md:text-[20px]">
        A tulip diagram is the small drawing at each waypoint of a
        <router-link
          to="/rally-roadbook"
          class="text-[#FF0000] hover:underline"
          >rally roadbook</router-link
        >
        that shows how to navigate a junction. The name comes from the
        flower-like shape: a dot marks where you enter the junction, and an arrow
        shows the road to take out of it. Tulips are the universal language of
        rally navigation &mdash; readable at speed, in any country, in any
        language.
      </p>
      <GuideDownloadCta />
      <picture class="contents">
        <source srcset="/images/CC-Roadbook-Image-qbqkj0exbp9dd466e7ex7ryf7wdwx9qdmhdcijtm1k.webp" type="image/webp" />
        <img
          decoding="async"
          fetchpriority="high"
          width="600"
          height="900"
          src="/images/CC-Roadbook-Image-qbqkj0exbp9dd466e7ex7ryf7wdwx9qdmhdcijtm1k.png"
          class="flex m-auto"
          alt="Rally roadbook page showing tulip diagrams with dot, arrow and navigation icons"
        />
      </picture>
    </div>

    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        How to Read a Tulip Diagram
      </h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-3 font-arial text-[16px] md:text-[18px] font-bold"
      >
        <li>
          <span class="text-white">The dot</span> marks your position entering
          the junction &mdash; always read the tulip from the dot.
        </li>
        <li>
          <span class="text-white">The arrow</span> (often a ball-and-arrow,
          or "bolletje pijltje") shows the road you must take out.
        </li>
        <li>
          <span class="text-white">The lines</span> are the other roads and
          tracks at the junction, drawn to match the real angles.
        </li>
        <li>
          <span class="text-white">Icons &amp; notes</span> add hazards,
          surface changes, danger levels and landmarks around the tulip.
        </li>
        <li>
          <span class="text-white">Distances &amp; CAP heading</span> sit beside
          the tulip: the partial to the next waypoint and the total, plus the
          compass bearing.
        </li>
      </ul>
    </div>

    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        How Rally Navigator Draws Tulips for You
      </h2>
      <p class="text-[17px] md:text-[20px]">
        You don't draw tulips by hand. As you build your route on the
        <router-link to="/rally-maps" class="text-[#FF0000] hover:underline"
          >map</router-link
        >, Rally Navigator draws the route arrow at every waypoint from your
        track. You add the side roads and shape them to match the junction
        exactly, drag and drop FIA / Dakar navigation icons, and add text notes.
        Pro users can let Tulip Assist draft the side roads and no-go lines from
        the map. The result prints straight into a clean, FIA-compliant roadbook
        PDF.
      </p>
      <picture class="contents">
        <source srcset="/images/Road-Rally-Roadbook-qbqkj0exbp9dd466e7ex7ryf7wdwx9qdmhdcijtm1k.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="587"
          height="939"
          src="/images/Road-Rally-Roadbook-qbqkj0exbp9dd466e7ex7ryf7wdwx9qdmhdcijtm1k.png"
          class="flex m-auto"
          alt="Road rally roadbook with auto-generated tulip diagrams for each waypoint"
        />
      </picture>
      <router-link to="/download" class="m-auto">
        <h2
          class="text-[#FF0000] font-russo text-[22px] md:text-[28px] hover:underline text-center"
        >
          Download Rally Navigator and Draw Your First Tulips Free
        </h2>
      </router-link>
    </div>
    <GuideDownloadCta />
  </section>

  <section class="my-8 mx-auto max-w-[1400px]">
    <FeaturesBanner />
    <PartnersSmall />
    <RallyLogoDownload />
    <div class="py-8 mt-8 border-t border-white/50">
      <p class="text-[17px] md:text-[20px]">
        <strong class="text-white">Drawing a lot of tulips?</strong> Tulip Assist drafts each tulip's roads and no-go lines from the map for you to review and approve. It's one of the features a Pro Rally licence unlocks.
        <router-link to="/pro-features" class="text-[#FF0000] hover:underline"
          >See every Pro Rally feature &rarr;</router-link
        >
      </p>
    </div>
  </section>
  <RelatedGuides
    :links="[
        { to: '/how-to-create-a-cross-country', text: 'How to Create a Cross Country Roadbook' },
        { to: '/how-to-create-a-road-rally', text: 'How to Create a Road Rally Roadbook' },
        { to: '/rally-maps', text: 'Rally Route Planner' },
        { to: '/rally-roadbook', text: 'What Is a Rally Roadbook?' },
      ]"
  />
</template>

<script>
import FeaturesBanner from './FeaturesBanner.vue';
import RelatedGuides from './RelatedGuides.vue';
import GuideDownloadCta from './GuideDownloadCta.vue';
import PartnersSmall from './PartnersSmall.vue';
import RallyLogoDownload from './RallyLogoDownload.vue';

export default {
  name: 'RallyTulipDiagrams',
  components: {
    GuideDownloadCta,
    FeaturesBanner,
    RelatedGuides,
    PartnersSmall,
    RallyLogoDownload,
  },
};
</script>

<style scoped></style>
